<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { inject, onMounted, onUnmounted } from 'vue'
import { cn } from '../utils'
import { usePromptInputProvider } from './context'
import type { PromptInputContext, PromptInputMessage } from './types'
import { PROMPT_INPUT_KEY } from './types'

const props = defineProps<{
  class?: HTMLAttributes['class']
  accept?: string
  multiple?: boolean
  globalDrop?: boolean
  maxFiles?: number
  maxFileSize?: number
  initialInput?: string
}>()

const emit = defineEmits<{
  (e: 'submit', payload: PromptInputMessage): void
  (e: 'error', payload: { code: string; message: string }): void
}>()

const inheritedContext = inject(PROMPT_INPUT_KEY, null)
const localContext: PromptInputContext | null = inheritedContext
  ? null
  : usePromptInputProvider({
      initialInput: props.initialInput,
      maxFiles: props.maxFiles,
      maxFileSize: props.maxFileSize,
      accept: props.accept,
      onSubmit: (msg) => emit('submit', msg),
      onError: (err) => emit('error', err),
    })

const context = inheritedContext || localContext

if (!context) {
  throw new Error('PromptInput context is missing.')
}

const { fileInputRef, addFiles, submitForm } = context

function setFileInputRef(element: unknown) {
  fileInputRef.value = element as HTMLInputElement | null
}

function handleDragOver(e: DragEvent) {
  if (e.dataTransfer?.types?.includes('Files')) {
    e.preventDefault()
  }
}

function handleDrop(e: DragEvent) {
  if (e.dataTransfer?.types?.includes('Files')) {
    e.preventDefault()
  }

  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    addFiles(e.dataTransfer.files)
  }
}

onMounted(() => {
  if (props.globalDrop) {
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)
  }
})

onUnmounted(() => {
  if (props.globalDrop) {
    document.removeEventListener('dragover', handleDragOver)
    document.removeEventListener('drop', handleDrop)
  }
})

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files) {
    addFiles(input.files)
  }
  input.value = ''
}

function onSubmit(e: Event) {
  e.preventDefault()
  submitForm()
}
</script>

<template>
  <div>
    <input
      :ref="setFileInputRef"
      type="file"
      class="hidden"
      :accept="props.accept"
      :multiple="props.multiple"
      @change="onFileChange"
    />
    <form
      :class="cn('w-full', props.class)"
      @submit="onSubmit"
      @dragover.prevent="handleDragOver"
      @drop.prevent.stop="handleDrop"
    >
      <div
        class="flex w-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm transition focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 dark:border-white/10 dark:bg-gray-800"
      >
        <slot />
      </div>
    </form>
  </div>
</template>
